import { useState, useEffect, useRef, useCallback } from "react";
import { Volume2, VolumeX, Play } from "lucide-react";
import { api } from "@/App";

let currentPlaying = null; // avoid simultaneous playback across the feed

export const PostVideo = ({ post, className = "" }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const viewTracked = useRef(false);
  const src = post.media_urls?.[0] || post.image_url;

  const trackView = useCallback(() => {
    if (viewTracked.current) return;
    viewTracked.current = true;
    api.post(`/photos/${post.photo_id}/video-view`).catch(() => {});
  }, [post.photo_id]);

  useEffect(() => {
    const v = videoRef.current;
    const el = containerRef.current;
    if (!v || !el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          if (currentPlaying && currentPlaying !== v) currentPlaying.pause();
          currentPlaying = v;
          v.play().then(() => { setPlaying(true); trackView(); }).catch(() => {});
        } else {
          v.pause();
          setPlaying(false);
        }
      },
      { threshold: [0, 0.6] }
    );
    observer.observe(el);
    const onVisibility = () => { if (document.hidden) { v.pause(); setPlaying(false); } };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      if (currentPlaying === v) currentPlaying = null;
      v.pause();
    };
  }, [trackView]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      if (currentPlaying && currentPlaying !== v) currentPlaying.pause();
      currentPlaying = v;
      v.play().then(() => { setPlaying(true); trackView(); }).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative bg-black ${className}`} data-testid="post-video">
      <video
        ref={videoRef}
        src={`${src}#t=0.001`}
        className="w-full max-h-[560px] object-contain"
        muted={muted}
        loop
        playsInline
        preload="metadata"
        onClick={togglePlay}
      />
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-7 h-7 text-white ml-1" fill="white" />
          </div>
        </div>
      )}
      <button
        type="button"
        data-testid="post-video-mute-btn"
        onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }}
        className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center active:scale-90 transition-transform"
      >
        {muted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-white" />}
      </button>
    </div>
  );
};
